/** UX2 copy helpers — humanize PMV codes for business UI. See docs/pmv/ux2/PMV-UX2-TERMINOLOGY.md */

export type PmvAttentionSeverity = 'critical' | 'warning' | 'information';

export type PmvAttentionItem = {
  id: string;
  severity: PmvAttentionSeverity;
  title: string;
  explanation: string;
  count: number;
  href: string;
  actionLabel: string;
};

export function humanizeFuelExceptionReason(reason: string): string {
  const r = String(reason || '').trim().toUpperCase();
  if (r === 'MISSING_PROJECT') return 'Project attribution missing';
  if (r === 'MISSING_COST') return 'Fuel cost missing';
  if (r === 'HIGH_VARIANCE' || r === 'FUEL_VARIANCE') return 'Fuel variance';
  if (r.includes('PROJECT')) return 'Project attribution issue';
  return reason.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

/** Present readiness section keys for business UI (existing DTO only). */
export function humanizeReadinessSectionKey(key: string): string {
  const k = String(key || '').trim().toUpperCase();
  const map: Record<string, string> = {
    TECHNICAL: 'Technical',
    INSPECTION: 'Inspection',
    DOCUMENTATION: 'Documents',
    DOCUMENTS: 'Documents',
    REGULATORY: 'Insurance',
    INSURANCE: 'Insurance',
    MAINTENANCE: 'Maintenance',
    OPERATOR: 'Operator',
    IDENTITY: 'Identity',
    OWNERSHIP: 'Ownership',
    STATUS: 'Status',
    METER: 'Meter',
    ALLOCATION: 'Allocation',
  };
  if (map[k]) return map[k];
  return k
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Overall readiness badge: READY / WARNING / BLOCKED from DTO overall. */
export function humanizeReadinessOverall(overall: string): {
  label: string;
  color: 'success' | 'warning' | 'error' | 'default';
} {
  const o = String(overall || '').trim().toUpperCase();
  if (o === 'PASS' || o === 'READY') return { label: 'READY', color: 'success' };
  if (o === 'WARNING') return { label: 'WARNING', color: 'warning' };
  if (o === 'BLOCKED' || o === 'FAIL') return { label: 'BLOCKED', color: 'error' };
  return { label: o || '—', color: 'default' };
}

export function humanizeAttentionPhrase(raw: string): string {
  return String(raw || '')
    .replace(/insurance expiring ≤30d/gi, 'insurance expiring within 30 days')
    .replace(/fuel exception\(s\)/gi, 'fuel exceptions')
    .replace(/open breakdown\(s\)/gi, 'open breakdowns')
    .replace(/PM overdue/gi, 'preventive maintenance overdue');
}

export function explainNullUtilization(missingLogs: number): string {
  if (missingLogs > 0) {
    return `Utilization unavailable — ${missingLogs} expected daily log${missingLogs === 1 ? '' : 's'} not certified.`;
  }
  return 'Utilization unavailable — certified daily logs required.';
}

export function explainNullAvailability(missingLogs: number): string {
  if (missingLogs > 0) {
    return `Availability unavailable — ${missingLogs} expected daily log${missingLogs === 1 ? '' : 's'} not certified.`;
  }
  return 'Availability unavailable — certified daily logs required.';
}

/** Compose attention cards from Control Tower snapshot fields (no fabricated signals). */
export function buildCommandCenterAttention(input: {
  missingLogs: number;
  openBreakdowns: number;
  breakdownUnits: number;
  pmDue: number;
  pmDue7d: number;
  complianceExpiring30d: number;
  quarantined: number;
  fuelExceptions: number;
  backendAttention: string[];
}): PmvAttentionItem[] {
  const items: PmvAttentionItem[] = [];

  if (input.missingLogs > 0) {
    items.push({
      id: 'missing-logs',
      severity: 'critical',
      title: 'Daily logs missing',
      explanation: `${input.missingLogs} unit${input.missingLogs === 1 ? '' : 's'} expected on site without a certified daily log today.`,
      count: input.missingLogs,
      href: '/asset/pmv/daily-logs',
      actionLabel: 'Review missing logs',
    });
  }

  if (input.openBreakdowns > 0 || input.breakdownUnits > 0) {
    const n = Math.max(input.openBreakdowns, input.breakdownUnits);
    items.push({
      id: 'breakdowns',
      severity: 'critical',
      title: 'Open breakdowns',
      explanation: `${n} breakdown event${n === 1 ? '' : 's'} or unit${n === 1 ? '' : 's'} currently unavailable for breakdown.`,
      count: n,
      href: '/asset/pmv/operations',
      actionLabel: 'Open deployment board',
    });
  }

  if (input.pmDue > 0) {
    items.push({
      id: 'pm-overdue',
      severity: 'critical',
      title: 'Preventive maintenance overdue',
      explanation: `${input.pmDue} unit${input.pmDue === 1 ? '' : 's'} past PM due date or meter interval.`,
      count: input.pmDue,
      href: '/asset/pmv?attention=pm_due',
      actionLabel: 'Open fleet',
    });
  }

  if (input.quarantined > 0) {
    items.push({
      id: 'quarantined',
      severity: 'critical',
      title: 'Compliance hold',
      explanation: `${input.quarantined} unit${input.quarantined === 1 ? '' : 's'} quarantined or held for compliance.`,
      count: input.quarantined,
      href: '/asset/pmv?status=QUARANTINED',
      actionLabel: 'Open fleet',
    });
  }

  if (input.complianceExpiring30d > 0) {
    items.push({
      id: 'compliance-expiring',
      severity: 'warning',
      title: 'Insurance / compliance expiring',
      explanation: `${input.complianceExpiring30d} unit${input.complianceExpiring30d === 1 ? '' : 's'} with insurance or compliance items expiring within 30 days.`,
      count: input.complianceExpiring30d,
      href: '/asset/pmv',
      actionLabel: 'Review fleet',
    });
  }

  if (input.pmDue7d > 0) {
    items.push({
      id: 'pm-due-soon',
      severity: 'warning',
      title: 'Preventive maintenance due soon',
      explanation: `${input.pmDue7d} unit${input.pmDue7d === 1 ? '' : 's'} with PM due within 7 days.`,
      count: input.pmDue7d,
      href: '/asset/pmv?attention=pm_due_7d',
      actionLabel: 'Open fleet',
    });
  }

  if (input.fuelExceptions > 0) {
    items.push({
      id: 'fuel-exceptions',
      severity: 'warning',
      title: 'Fuel exceptions',
      explanation: `${input.fuelExceptions} fuel attribution exception${input.fuelExceptions === 1 ? '' : 's'} need review (for example project attribution missing).`,
      count: input.fuelExceptions,
      href: '/asset/pmv/control-tower#fuel-exceptions',
      actionLabel: 'Review fuel exceptions',
    });
  }

  for (const raw of input.backendAttention) {
    const phrase = humanizeAttentionPhrase(raw);
    const id = `backend-${raw}`;
    if (items.some((i) => phrase.toLowerCase().includes(i.title.toLowerCase().slice(0, 12)))) continue;
    items.push({
      id,
      severity: 'information',
      title: phrase,
      explanation: 'Reported by PMV Command Center attention summary.',
      count: 1,
      href: '/asset/pmv/operations',
      actionLabel: 'Open operations',
    });
  }

  const order: Record<PmvAttentionSeverity, number> = { critical: 0, warning: 1, information: 2 };
  return items.sort((a, b) => order[a.severity] - order[b.severity]);
}

/** Context-sensitive primary CTA for Equipment Passport — domain actions only. */
export function passportPrimaryAction(input: {
  operationalStatus: string;
  allocationStatus?: string | null;
  profileId: string;
  allocationId?: string | null;
}): { label: string; href: string } | null {
  const st = String(input.operationalStatus || '').toUpperCase();
  const alloc = String(input.allocationStatus || '').toUpperCase();
  const id = input.profileId;

  if (st === 'BREAKDOWN' || st === 'UNDER_MAINTENANCE') {
    return { label: 'Open maintenance context', href: `/asset/pmv/${id}?tab=maintenance` };
  }
  if (st === 'QUARANTINED' || st === 'UNDER_INSPECTION') {
    return { label: 'Review inspection & compliance', href: `/asset/pmv/${id}?tab=inspection` };
  }
  if (alloc === 'IN_TRANSIT' && input.allocationId) {
    return { label: 'Receive at site', href: `/asset/pmv/allocations/${input.allocationId}/receive` };
  }
  if (alloc === 'DISPATCH_READY' && input.allocationId) {
    return { label: 'Open dispatch', href: `/asset/pmv/allocations/${input.allocationId}` };
  }
  if (st === 'RESERVED' && input.allocationId) {
    return { label: 'Prepare dispatch', href: `/asset/pmv/allocations/${input.allocationId}` };
  }
  if (st === 'DEPLOYED' || alloc === 'ON_SITE' || alloc === 'ACTIVE' || alloc === 'DEPLOYED') {
    return { label: 'Daily operations', href: `/asset/pmv/daily-logs` };
  }
  if (st === 'AVAILABLE') {
    return { label: 'Equipment requests', href: `/asset/pmv/requests` };
  }
  return null;
}

